import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronLeft,
  FilePlus2,
  FileSearch,
  Info,
  Loader2,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import apiService from '../../services/api';
import type {
  CompletionCase,
  CompletionEvidenceStatus,
  CompletionFinancialStatus,
  CompletionItem,
  CompletionReadinessLevel,
  Document as SourceDocument,
} from '../../types';
import {
  completionEvidenceDisplayLabel,
  completionEvidenceReviewCopy,
  completionResolutionLabels,
  isCompletionEvidenceApprovable,
  targetedCompletionOutcomeCopy,
} from '../../utils/completionWorkflow';

type ViewFilter = 'gaps' | 'all';
type TierFilter = 'all' | 'critical' | 'review';

const readinessLabels: Record<CompletionReadinessLevel, string> = {
  extracting: 'בחילוץ',
  completing: 'בהשלמת נתונים',
  ready_with_limitations: 'מוכן עם מגבלות',
  decision_ready: 'מוכן לניתוח',
};

const readinessClasses: Record<CompletionReadinessLevel, string> = {
  extracting: 'bg-primary-50 text-primary-800',
  completing: 'bg-warning-50 text-warning-800',
  ready_with_limitations: 'bg-warning-50 text-warning-900',
  decision_ready: 'bg-success-50 text-success-800',
};

const evidenceClasses = (
  status: CompletionEvidenceStatus,
  hasApprovableEvidence = false,
): string => {
  if (status === 'verified' || status === 'calculated') return 'bg-success-50 text-success-800';
  if (status === 'contradictory') return 'bg-error-50 text-error-800';
  if (status === 'candidate' || hasApprovableEvidence) return 'bg-warning-50 text-warning-800';
  return 'bg-gray-100 text-gray-700';
};

const itemHasApprovableEvidence = (item: CompletionItem): boolean => (
  item.evidence.some(isCompletionEvidenceApprovable)
);

const errorDetail = (error: unknown, fallback: string): string => (
  (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback
);

const isGap = (item: CompletionItem): boolean => (
  item.applicability !== 'not_applicable'
  && !['verified', 'calculated'].includes(item.evidence_status)
);

const CompletionWorkbench: React.FC<{ runId: string }> = ({ runId }) => {
  const queryClient = useQueryClient();
  const [viewFilter, setViewFilter] = useState<ViewFilter>('gaps');
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [note, setNote] = useState('');
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [financialStatus, setFinancialStatus] = useState<CompletionFinancialStatus>('not_assessed');
  const [activeJobId, setActiveJobId] = useState<string>('');
  const [evidenceForm, setEvidenceForm] = useState({
    value: '',
    unit: '',
    quote: '',
    section_title: '',
    page: '',
    source_document_id: '',
  });

  const caseQuery = useQuery({
    queryKey: ['completion-case-run', runId],
    queryFn: () => apiService.getCompletionCaseForRun(runId),
    enabled: !!runId,
    retry: false,
  });
  const completionCase = caseQuery.data as CompletionCase | undefined;
  const itemsQuery = useQuery({
    queryKey: ['completion-items', completionCase?.case_id],
    queryFn: () => apiService.getCompletionItems(completionCase!.case_id),
    enabled: !!completionCase?.case_id,
  });
  const targetedJobQuery = useQuery({
    queryKey: ['completion-targeted-job', activeJobId],
    queryFn: () => apiService.getCompletionJob(activeJobId),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2000 : false;
    },
  });
  const documentsQuery = useQuery({
    queryKey: ['completion-source-documents'],
    queryFn: () => apiService.getDocuments({ page: 1, size: 100 }),
    enabled: showEvidenceForm,
    staleTime: 60_000,
  });

  const openCase = useMutation({
    mutationFn: () => apiService.openCompletionCase(runId),
    onSuccess: (data) => {
      queryClient.setQueryData(['completion-case-run', runId], data);
      toast.success('תיק ההשלמה נפתח');
    },
    onError: (error) => toast.error(errorDetail(error, 'פתיחת תיק ההשלמה נכשלה')),
  });

  const resolveItem = useMutation({
    mutationFn: (args: {
      itemKey: string;
      action: 'approve_candidate' | 'mark_not_applicable' | 'mark_not_disclosed' | 'mark_external_required' | 'accept_limitation' | 'set_financial_status';
      reasonCode: string;
      evidenceId?: string;
      noteText?: string;
      risk?: CompletionFinancialStatus;
    }) => apiService.resolveCompletionItem(completionCase!.case_id, args.itemKey, {
      action: args.action,
      reason_code: args.reasonCode,
      evidence_id: args.evidenceId,
      note: args.noteText,
      financial_status: args.risk,
    }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['completion-case-run', runId], data);
      void queryClient.invalidateQueries({ queryKey: ['completion-items', data.case_id] });
      setNote('');
      toast.success(
        variables.action === 'approve_candidate'
          ? 'הראיה אושרה, הסטטוס עודכן והסעיף הוסר מרשימת הפערים'
          : 'הפריט עודכן',
        { duration: 5000 },
      );
    },
    onError: (error) => toast.error(errorDetail(error, 'עדכון הפריט נכשל')),
  });

  const addEvidence = useMutation({
    mutationFn: (itemKey: string) => apiService.addCompletionEvidence(
      completionCase!.case_id,
      itemKey,
      {
        value: evidenceForm.value.trim(),
        unit: evidenceForm.unit.trim() || undefined,
        quote: evidenceForm.quote.trim(),
        section_title: evidenceForm.section_title.trim() || undefined,
        page: evidenceForm.page ? Number(evidenceForm.page) : undefined,
        source_document_id: evidenceForm.source_document_id
          ? Number(evidenceForm.source_document_id)
          : undefined,
        source_type: 'uploaded_document',
        scope: 'unknown',
      },
    ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['completion-items', completionCase?.case_id] });
      void queryClient.invalidateQueries({ queryKey: ['completion-case-run', runId] });
      setEvidenceForm({ value: '', unit: '', quote: '', section_title: '', page: '', source_document_id: '' });
      setShowEvidenceForm(false);
      toast.success('הראיה נוספה וממתינה לאישור');
    },
    onError: (error) => toast.error(errorDetail(error, 'הוספת הראיה נכשלה')),
  });

  const validateCase = useMutation({
    mutationFn: () => apiService.validateCompletionCase(completionCase!.case_id),
    onSuccess: (data) => {
      queryClient.setQueryData(['completion-case-run', runId], data);
      toast.success('שער המוכנות חושב מחדש');
    },
    onError: (error) => toast.error(errorDetail(error, 'בדיקת המוכנות נכשלה')),
  });

  const finalizeCase = useMutation({
    mutationFn: () => apiService.finalizeCompletionCase(completionCase!.case_id),
    onSuccess: (data) => {
      queryClient.setQueryData(['completion-case-run', runId], data);
      toast.success('תיק ההשלמה נסגר');
    },
    onError: (error) => toast.error(errorDetail(error, 'לא ניתן לסגור את התיק')),
  });

  const retryItem = useMutation({
    mutationFn: (itemKey: string) => apiService.retryCompletionItem(completionCase!.case_id, itemKey),
    onSuccess: (job) => {
      setActiveJobId(job.job_id);
      void queryClient.invalidateQueries({ queryKey: ['completion-items', completionCase?.case_id] });
      toast.success('החילוץ הממוקד התחיל');
    },
    onError: (error) => toast.error(errorDetail(error, 'החילוץ הממוקד לא התחיל')),
  });

  const items = useMemo<CompletionItem[]>(() => itemsQuery.data || [], [itemsQuery.data]);
  const visibleItems = useMemo(() => items.filter((item) => {
    if (viewFilter === 'gaps' && !isGap(item)) return false;
    if (tierFilter === 'critical' && item.required_level !== 'critical') return false;
    if (tierFilter === 'review' && item.resolution_status !== 'manual_review') return false;
    return true;
  }), [items, viewFilter, tierFilter]);

  useEffect(() => {
    if (!visibleItems.some((item) => item.item_key === selectedKey)) {
      setSelectedKey(visibleItems[0]?.item_key || '');
    }
  }, [visibleItems, selectedKey]);

  const selectedItem = items.find((item) => item.item_key === selectedKey);
  const selectedItemKey = selectedItem?.item_key;
  const selectedItemFinancialStatus = selectedItem?.financial_status;
  useEffect(() => {
    if (selectedItemFinancialStatus) setFinancialStatus(selectedItemFinancialStatus);
  }, [selectedItemFinancialStatus]);
  useEffect(() => {
    setNote('');
    setShowEvidenceForm(false);
  }, [selectedItemKey]);

  const targetedJobStatus = targetedJobQuery.data?.status;
  useEffect(() => {
    if (targetedJobStatus === 'done') {
      void queryClient.invalidateQueries({ queryKey: ['completion-items', completionCase?.case_id] });
      void queryClient.invalidateQueries({ queryKey: ['completion-case-run', runId] });
      toast.success(targetedCompletionOutcomeCopy(targetedJobQuery.data?.result?.status), { duration: 6000 });
      setActiveJobId('');
    } else if (targetedJobStatus === 'error') {
      toast.error(targetedJobQuery.data?.error || 'החילוץ הממוקד נכשל');
      setActiveJobId('');
    }
  }, [targetedJobStatus, targetedJobQuery.data?.error, targetedJobQuery.data?.result?.status, completionCase?.case_id, queryClient, runId]);

  const noCase = (
    (caseQuery.error as { response?: { status?: number } } | null)?.response?.status === 404
  );

  if (caseQuery.isLoading) {
    return <div className="mt-6 h-28 animate-pulse rounded-2xl bg-white motion-reduce:animate-none" />;
  }

  if (!completionCase && noCase) {
    return (
      <section className="mt-6 border-y border-gray-200 bg-white px-5 py-6 sm:px-6" aria-label="השלמת נתונים">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-gray-900">
              <FileSearch className="h-5 w-5 text-primary-600" />
              <h2 className="text-base font-semibold">השלמת נתונים לפני ניתוח</h2>
            </div>
            <p className="mt-1.5 max-w-2xl text-sm text-gray-500">
              סווג פערי חילוץ, אשר ראיות והפרד בין מידע חסר לבין סיכון פיננסי.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openCase.mutate()}
            disabled={openCase.isPending}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {openCase.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
            פתח תיק השלמה
          </button>
        </div>
      </section>
    );
  }

  if (!completionCase) {
    return (
      <section className="mt-6 border-y border-error-200 bg-error-50 px-5 py-4 text-sm text-error-800">
        טעינת תיק ההשלמה נכשלה. <button className="font-semibold underline" onClick={() => caseQuery.refetch()}>נסה שוב</button>
      </section>
    );
  }

  const stats = completionCase.stats;
  const isFinalized = completionCase.status === 'finalized';
  const canFinalize = ['decision_ready', 'ready_with_limitations'].includes(completionCase.readiness_level) && !isFinalized;
  const latestEvidence = selectedItem?.evidence[0];
  const approvableEvidence = selectedItem?.evidence.find(isCompletionEvidenceApprovable);
  const hasApprovableEvidence = Boolean(
    selectedItem && isGap(selectedItem) && approvableEvidence,
  );
  const displayedEvidence = hasApprovableEvidence ? approvableEvidence : latestEvidence;
  const evidenceReviewCopy = selectedItem
    ? completionEvidenceReviewCopy(selectedItem.evidence_status, hasApprovableEvidence)
    : undefined;

  return (
    <section className="mt-6 overflow-hidden border-y border-gray-200 bg-white" aria-labelledby="completion-title">
      <header className="flex flex-col gap-4 border-b border-gray-100 px-5 py-5 sm:px-6 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="completion-title" className="text-base font-semibold text-gray-950">תיק השלמת נתונים</h2>
            <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-800">מצב מעודכן</span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${readinessClasses[completionCase.readiness_level]}`}>
              {readinessLabels[completionCase.readiness_level]}
            </span>
            {isFinalized && <span className="inline-flex items-center gap-1 rounded-full bg-gray-900 px-2.5 py-1 text-xs font-medium text-white"><LockKeyhole className="h-3 w-3" /> נסגר</span>}
          </div>
          <p className="mt-1.5 text-sm text-gray-500">זהו המצב העדכני לאחר השלמות ואישורי אנליסט. רק ראיות מאומתות משפיעות על מוכנות הניתוח.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => validateCase.mutate()}
            disabled={validateCase.isPending}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${validateCase.isPending ? 'animate-spin' : ''}`} /> בדוק מוכנות
          </button>
          <button
            type="button"
            onClick={() => finalizeCase.mutate()}
            disabled={!canFinalize || finalizeCase.isPending}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-gray-900 px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
          >
            <ShieldCheck className="h-4 w-4" /> סגור תיק
          </button>
        </div>
      </header>

      <div className="flex items-start gap-2 border-b border-primary-100 bg-primary-50/60 px-5 py-3 text-xs leading-5 text-primary-900 sm:px-6">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          <strong>כאן מתבצעים התיקונים.</strong> תוצאת החילוץ המקורית נשמרת בהמשך העמוד כתיעוד ואינה משתנה.
          {' '}<a href="#corpus-values-title" className="font-semibold underline decoration-primary-300 underline-offset-2">לתוצאת החילוץ המקורית</a>
        </p>
      </div>

      <div className="grid border-b border-gray-100 sm:grid-cols-3">
        {[
          { label: 'כיסוי ראיות', value: `${stats?.evidence_coverage_pct || 0}%`, detail: `${stats?.evidence_covered || 0} מתוך ${stats?.applicable_total || 0}` },
          { label: 'שלמות מקור', value: `${stats?.provenance_integrity_pct || 0}%`, detail: 'מקור, ציטוט וייחוס' },
          { label: 'ליבה קריטית', value: `${stats?.critical_covered || 0}/${stats?.critical_total || 0}`, detail: `${stats?.open_gaps || 0} פערים פתוחים` },
        ].map((metric, index) => (
          <div key={metric.label} className={`px-5 py-4 sm:px-6 ${index ? 'border-t border-gray-100 sm:border-r sm:border-t-0' : ''}`}>
            <div className="text-2xl font-semibold tabular-nums text-gray-950">{metric.value}</div>
            <div className="mt-1 text-xs font-semibold text-gray-600">{metric.label}</div>
            <div className="mt-0.5 text-[11px] text-gray-400">{metric.detail}</div>
          </div>
        ))}
      </div>

      <div className="grid min-h-[430px] lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 px-4 py-5 sm:px-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
              {([['gaps', 'פערים'], ['all', 'כל הסעיפים']] as Array<[ViewFilter, string]>).map(([value, label]) => (
                <button key={value} type="button" onClick={() => setViewFilter(value)} className={`min-h-9 rounded-md px-3 text-xs font-semibold transition-colors ${viewFilter === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>{label}</button>
              ))}
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {([['all', 'הכל'], ['critical', 'קריטי'], ['review', 'לבדיקה']] as Array<[TierFilter, string]>).map(([value, label]) => (
                <button key={value} type="button" onClick={() => setTierFilter(value)} className={`min-h-9 whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors ${tierFilter === value ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>{label}</button>
              ))}
            </div>
          </div>

          {itemsQuery.isLoading ? (
            <div className="h-64 animate-pulse rounded-xl bg-gray-100 motion-reduce:animate-none" />
          ) : visibleItems.length === 0 ? (
            <div className="flex min-h-60 flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-9 w-9 text-success-500" />
              <p className="mt-3 text-sm font-semibold text-gray-800">אין פערים בסינון הנוכחי</p>
              <p className="mt-1 text-xs text-gray-500">אפשר לעבור לכל הסעיפים או לבדוק את שער המוכנות.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 border-y border-gray-100">
              {visibleItems.map((item) => {
                const hasReviewableEvidence = itemHasApprovableEvidence(item);
                return (
                  <button
                    key={item.item_key}
                    type="button"
                    onClick={() => setSelectedKey(item.item_key)}
                    className={`grid w-full grid-cols-[64px_minmax(0,1fr)_auto] items-center gap-3 px-2 py-3 text-right transition-colors ${selectedKey === item.item_key ? 'bg-primary-50/70' : 'hover:bg-gray-50'}`}
                  >
                    <span className="font-mono text-xs text-gray-400" dir="ltr">{item.item_key}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900">{item.name || item.item_key}</span>
                      <span className="mt-0.5 block truncate text-xs text-gray-400">{item.category}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      {item.required_level === 'critical' && <span className="text-[10px] font-bold uppercase tracking-wide text-error-700">קריטי</span>}
                      <span className={`rounded-full px-2 py-1 text-[11px] font-medium ${evidenceClasses(item.evidence_status, hasReviewableEvidence)}`}>
                        {completionEvidenceDisplayLabel(item.evidence_status, hasReviewableEvidence)}
                      </span>
                      <ChevronLeft className="h-4 w-4 text-gray-300" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <aside className="border-t border-gray-200 bg-gray-50/70 px-5 py-5 lg:border-r lg:border-t-0" aria-label="פרטי פער וראיות">
          {selectedItem ? (
            <div key={selectedItem.item_key} className="transition-opacity duration-150">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-xs text-gray-400" dir="ltr">{selectedItem.item_key}</div>
                  <h3 className="mt-1 text-sm font-semibold leading-6 text-gray-950">{selectedItem.name || selectedItem.item_key}</h3>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-medium ${evidenceClasses(selectedItem.evidence_status, hasApprovableEvidence)}`}>
                  {completionEvidenceDisplayLabel(selectedItem.evidence_status, hasApprovableEvidence)}
                </span>
              </div>

              <dl className="mt-4 divide-y divide-gray-200 text-xs">
                <div className="grid grid-cols-[88px_1fr] gap-2 py-2"><dt className="text-gray-500">חובת השלמה</dt><dd className="font-medium text-gray-800">{selectedItem.required_level === 'critical' ? 'קריטי' : selectedItem.required_level === 'required' ? 'נדרש' : 'לפי תחולה'}</dd></div>
                <div className="grid grid-cols-[88px_1fr] gap-2 py-2"><dt className="text-gray-500">הנחיית מקור</dt><dd className="text-gray-700">{selectedItem.source_hint || '—'}</dd></div>
                <div className="grid grid-cols-[88px_1fr] gap-2 py-2"><dt className="text-gray-500">מצב טיפול</dt><dd className="font-medium text-gray-700">{completionResolutionLabels[selectedItem.resolution_status]}</dd></div>
              </dl>

              {displayedEvidence && (
                <div className={`mt-4 border-r-2 py-2 pr-3 ${hasApprovableEvidence || selectedItem.evidence_status === 'contradictory' ? 'border-warning-400 bg-warning-50/60 pl-3' : 'border-primary-300'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-semibold text-gray-500">{hasApprovableEvidence ? 'ראיה זמינה לאישור' : 'ראיה אחרונה'}</div>
                    {hasApprovableEvidence && selectedItem.evidence_status !== 'contradictory' && <span className="text-[10px] font-bold text-warning-800">נמצא ערך · טרם אושר</span>}
                    {selectedItem.evidence_status === 'contradictory' && <span className="text-[10px] font-bold text-error-700">נדרשת הכרעה</span>}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-gray-900" dir="auto">{displayedEvidence.value || '—'} {displayedEvidence.unit || ''}</div>
                  {displayedEvidence.quote && <blockquote className="mt-2 line-clamp-4 text-xs leading-5 text-gray-600" dir="auto">{displayedEvidence.quote}</blockquote>}
                  <div className="mt-2 text-[11px] text-gray-400">{displayedEvidence.section_title || 'ללא פרק'}{displayedEvidence.page ? ` · עמוד ${displayedEvidence.page}` : ''}</div>
                  {evidenceReviewCopy && <p className="mt-2 text-[11px] font-medium leading-5 text-warning-900">{evidenceReviewCopy}</p>}
                </div>
              )}

              {!isFinalized && (
                <div className="mt-5 space-y-3">
                  {isGap(selectedItem) && approvableEvidence && (
                    <button
                      type="button"
                      onClick={() => resolveItem.mutate({ itemKey: selectedItem.item_key, action: 'approve_candidate', reasonCode: 'analyst_verified_source', evidenceId: approvableEvidence.evidence_id })}
                      disabled={resolveItem.isPending}
                      className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg bg-success-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-success-700 disabled:opacity-50"
                    >
                      <Check className="h-4 w-4" /> {selectedItem.evidence_status === 'contradictory' ? 'בחר ראיה זו ופתור את הסתירה' : 'אשר ראיה ופתור את הפער'}
                    </button>
                  )}

                  {selectedItem.evidence.length > 1 && (
                    <details className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                      <summary className="cursor-pointer text-xs font-semibold text-gray-700">
                        היסטוריית ראיות ({selectedItem.evidence.length})
                      </summary>
                      <div className="mt-2 divide-y divide-gray-100">
                        {selectedItem.evidence.map((evidence) => (
                          <div key={evidence.evidence_id} className="py-2 text-xs">
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-semibold text-gray-900" dir="auto">
                                {evidence.value || '—'} {evidence.unit || ''}
                              </span>
                              {isGap(selectedItem) && isCompletionEvidenceApprovable(evidence) && (
                                <button
                                  type="button"
                                  onClick={() => resolveItem.mutate({
                                    itemKey: selectedItem.item_key,
                                    action: 'approve_candidate',
                                    reasonCode: 'analyst_selected_evidence',
                                    evidenceId: evidence.evidence_id,
                                  })}
                                  disabled={resolveItem.isPending}
                                  className="shrink-0 font-semibold text-primary-700 hover:text-primary-800 disabled:opacity-50"
                                >
                                  בחר ואשר
                                </button>
                              )}
                            </div>
                            <div className="mt-1 text-[11px] text-gray-400">
                              {evidence.section_title || 'ללא פרק'}
                              {evidence.page ? ` · עמוד ${evidence.page}` : ''}
                              {evidence.grounded === true ? ' · מעוגן' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {isGap(selectedItem) && selectedItem.evidence_status !== 'candidate' && (
                    <button
                      type="button"
                      onClick={() => retryItem.mutate(selectedItem.item_key)}
                      disabled={retryItem.isPending || !!activeJobId}
                      className={`inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-wait disabled:opacity-50 ${hasApprovableEvidence ? 'border border-primary-200 bg-white text-primary-700 hover:bg-primary-50' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                    >
                      {(retryItem.isPending || !!activeJobId) ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      {hasApprovableEvidence ? 'נסה חילוץ מחדש במקום זאת' : 'נסה חילוץ ממוקד'}
                    </button>
                  )}

                  <button type="button" onClick={() => setShowEvidenceForm((value) => !value)} className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">
                    {showEvidenceForm ? <X className="h-4 w-4" /> : <FilePlus2 className="h-4 w-4" />}
                    {showEvidenceForm ? 'סגור הוספה' : 'הוסף ראיה ממסמך'}
                  </button>

                  {showEvidenceForm && (
                    <form
                      className="space-y-2 border-t border-gray-200 pt-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (!evidenceForm.value.trim() || !evidenceForm.quote.trim()) return;
                        addEvidence.mutate(selectedItem.item_key);
                      }}
                    >
                      <select
                        value={evidenceForm.source_document_id}
                        onChange={(event) => setEvidenceForm((form) => ({ ...form, source_document_id: event.target.value }))}
                        className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-primary-400"
                        aria-label="מסמך מקור לראיה"
                      >
                        <option value="">הדוח הנוכחי</option>
                        {(documentsQuery.data?.items || [])
                          .filter((document: SourceDocument) => document.status === 'processed')
                          .map((document: SourceDocument) => (
                            <option key={document.id} value={document.id}>
                              {document.original_filename || document.filename}
                            </option>
                          ))}
                      </select>
                      <div className="grid grid-cols-[1fr_110px] gap-2">
                        <input required value={evidenceForm.value} onChange={(event) => setEvidenceForm((form) => ({ ...form, value: event.target.value }))} placeholder="ערך" className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-primary-400" />
                        <input value={evidenceForm.unit} onChange={(event) => setEvidenceForm((form) => ({ ...form, unit: event.target.value }))} placeholder="יחידה" className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-primary-400" />
                      </div>
                      <textarea required rows={3} value={evidenceForm.quote} onChange={(event) => setEvidenceForm((form) => ({ ...form, quote: event.target.value }))} placeholder="ציטוט מדויק מהמקור" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary-400" />
                      <div className="grid grid-cols-[1fr_90px] gap-2">
                        <input value={evidenceForm.section_title} onChange={(event) => setEvidenceForm((form) => ({ ...form, section_title: event.target.value }))} placeholder="שם פרק" className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-primary-400" />
                        <input type="number" min="1" value={evidenceForm.page} onChange={(event) => setEvidenceForm((form) => ({ ...form, page: event.target.value }))} placeholder="עמוד" className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-primary-400" />
                      </div>
                      <button type="submit" disabled={addEvidence.isPending} className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">{addEvidence.isPending && <Loader2 className="h-4 w-4 animate-spin" />} שמור כמועמד</button>
                    </form>
                  )}

                  {isGap(selectedItem) && (
                    <div className="border-t border-gray-200 pt-3">
                      <label className="text-[11px] font-semibold text-gray-600" htmlFor={`completion-note-${selectedItem.item_key}`}>סיבה או מגבלה</label>
                      <textarea id={`completion-note-${selectedItem.item_key}`} rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="הסבר קצר ואסמכתה לסיווג" className="mt-1.5 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary-400" />
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button type="button" disabled={!note.trim() || resolveItem.isPending} onClick={() => resolveItem.mutate({ itemKey: selectedItem.item_key, action: 'mark_not_applicable', reasonCode: 'profile_not_applicable', noteText: note.trim() })} className="min-h-10 rounded-lg border border-gray-300 bg-white px-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40">לא חל</button>
                        <button type="button" disabled={!note.trim() || resolveItem.isPending} onClick={() => resolveItem.mutate({ itemKey: selectedItem.item_key, action: 'mark_external_required', reasonCode: 'external_source_required', noteText: note.trim() })} className="min-h-10 rounded-lg border border-gray-300 bg-white px-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40">נדרש מקור חיצוני</button>
                        <button type="button" disabled={!note.trim() || resolveItem.isPending} onClick={() => resolveItem.mutate({ itemKey: selectedItem.item_key, action: 'mark_not_disclosed', reasonCode: 'issuer_not_disclosed', noteText: note.trim() })} className="min-h-10 rounded-lg border border-gray-300 bg-white px-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40">לא פורסם</button>
                        <button type="button" disabled={!note.trim() || resolveItem.isPending} onClick={() => resolveItem.mutate({ itemKey: selectedItem.item_key, action: 'accept_limitation', reasonCode: 'accepted_analysis_limitation', noteText: note.trim() })} className="min-h-10 rounded-lg border border-warning-300 bg-warning-50 px-2 text-xs font-semibold text-warning-900 hover:bg-warning-100 disabled:opacity-40">קבל כמגבלה</button>
                      </div>
                    </div>
                  )}

                  {['verified', 'calculated'].includes(selectedItem.evidence_status) && (
                    <div className="border-t border-gray-200 pt-3">
                      <label className="text-[11px] font-semibold text-gray-600" htmlFor={`financial-status-${selectedItem.item_key}`}>משמעות פיננסית</label>
                      <div className="mt-1.5 flex gap-2">
                        <select id={`financial-status-${selectedItem.item_key}`} value={financialStatus} onChange={(event) => setFinancialStatus(event.target.value as CompletionFinancialStatus)} className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2 text-xs outline-none focus:border-primary-400">
                          <option value="not_assessed">טרם הוערך</option>
                          <option value="positive">חיובי</option>
                          <option value="neutral">ניטרלי</option>
                          <option value="warning">אזהרה</option>
                          <option value="critical">קריטי</option>
                        </select>
                        <button type="button" onClick={() => resolveItem.mutate({ itemKey: selectedItem.item_key, action: 'set_financial_status', reasonCode: 'analyst_financial_assessment', risk: financialStatus })} className="h-10 rounded-lg bg-gray-900 px-3 text-xs font-semibold text-white hover:bg-gray-800">שמור</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-72 flex-col items-center justify-center text-center text-gray-500">
              <FileSearch className="h-8 w-8 text-gray-300" />
              <p className="mt-3 text-sm">בחר סעיף כדי לבדוק את הראיות ולטפל בפער.</p>
            </div>
          )}
        </aside>
      </div>

      {stats && ((stats.covenant_breaches || 0) > 0 || (stats.covenant_near_limits || 0) > 0) && (
        <div className="flex items-start gap-2 border-t border-warning-200 bg-warning-50 px-5 py-3 text-xs text-warning-900 sm:px-6">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          מנוע הקובננטים זיהה {stats.covenant_breaches || 0} הפרות ו־{stats.covenant_near_limits || 0} יחסים סמוכים לסף, על בסיס ראיות מאומתות בלבד.
        </div>
      )}

      {stats && stats.contradictions > 0 && (
        <div className="flex items-start gap-2 border-t border-error-200 bg-error-50 px-5 py-3 text-xs text-error-800 sm:px-6">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> קיימות {stats.contradictions} סתירות פתוחות. לא ניתן לסגור את התיק לפני הכרעה ביניהן.
        </div>
      )}
    </section>
  );
};

export default CompletionWorkbench;
